// WHAT: Utility functions for saving Supabase configuration to secure storage
// WHY:  Provides interface for persisting Supabase credentials securely
// DOMAIN: Settings → Supabase
// DEPENDENCIES: core.invoke for backend commands
// RISKS:
//   - NOTE: Error handling is minimal - may not provide detailed error messages
//   - NOTE: No validation for config values - may allow invalid data

import { core } from "@tauri-apps/api";

import { type SupabaseConfig } from "../types/SupabaseConfig";

/**
 * WHAT:
 *   Saves Supabase configuration (URL and API key) to secure storage.
 *
 * WHY:
 *   Persists Supabase credentials for application use.
 *
 * BEHAVIOR:
 *   - Invokes backend command to save config
 *   - Stores URL and key securely
 *
 * @param config
 *
 */
export async function setSupabaseConfig(config: SupabaseConfig): Promise<void> {
  await core.invoke("set_supabase_config", {
    key: config.key,
    url: config.url,
  });
}

/**
 * WHAT:
 *   Saves database URL to secure storage.
 *
 * WHY:
 *   Persists database URL for application use.
 *
 * BEHAVIOR:
 *   - Invokes backend command to save URL
 *   - Throws error if save fails
 *
 * @param url
 *
 */
export async function setDatabaseUrl(url: string): Promise<void> {
  await core.invoke("set_database_url", { url });
}
